import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import TrendingNow from './BlogComponents/TrendingNow'
import LatestPosts from './BlogComponents/LatestPosts'
import LatestTrends from './LatestTrends'
import { fetchBlogs } from '../lib/utils'
import CategoryListPage from './BlogComponents/CategoryListPage'

const LandingPage = () => {
  const [blogs, setBlogs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBlogPosts = useCallback(async () => {
    try {
      const data = await fetchBlogs()
      setBlogs(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBlogPosts()
  }, [fetchBlogPosts])

  const groupedBlogs = useMemo(() => {
    const grouped = {}
    blogs.forEach(blog => {
      blog.categories?.forEach(category => {
        if (!grouped[category.title]) {
          grouped[category.title] = []
        }
        grouped[category.title].push(blog)
      })
    })
    return grouped
  }, [blogs])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 50
      }
    }
  }

  return (
    <AnimatePresence>
      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <motion.div
            className="rounded-full h-32 w-32 border-t-4 border-b-4 border-blue-500"
            animate={{ rotate: 360 }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: 'linear'
            }}
          />
        </div>
      ) : error ? (
        <motion.div
          key="error"
          className="text-center py-10 text-red-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {error}
        </motion.div>
      ) : (
        <motion.div
          key="content"
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <LatestPosts title="Latest Posts" posts={blogs} layoutType="four-images" />
          </motion.div>

          <motion.div variants={itemVariants}>
            <LatestTrends title="Latest Trends" posts={blogs} layoutType="four-images" />
          </motion.div>
          <div className="mt-6">
            <CategoryListPage />
          </div>
          {Object.entries(groupedBlogs).map(([categoryTitle, articles]) => (
            <motion.div key={categoryTitle} variants={itemVariants}>
              <TrendingNow
                articles={articles}
                title={categoryTitle}
              />
            </motion.div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default LandingPage